import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import {
  FileDownload,
  FilterAlt,
  GridOn,
  Refresh,
  TableRows
} from '@mui/icons-material';
import LogsTable from './LogsTable';
import LogsFilters from './LogsFilters';
import LogsStatistics from './LogsStatistics';
import { activityLogsAPI } from '../../services/api';

const currentYear = new Date().getFullYear();

const DEFAULT_FILTERS = {
  search: '',
  log_level: [],
  classification: [],
  module: '',
  sub_module: '',
  action: '',
  performed_by: '',
  user_id: '',
  user_role: '',
  system_task: '',
  status: '',
  record_id: '',
  record_type: '',
  ip_address: '',
  request_id: '',
  error_code: '',
  date_from: `${currentYear}-01-01`,
  date_to: `${currentYear}-12-31`,
  page: 1,
  limit: 10,
  sort_by: 'created_at',
  sort_direction: 'desc'
};

const trackedFilterKeys = [
  'search',
  'log_level',
  'classification',
  'module',
  'sub_module',
  'action',
  'performed_by',
  'user_id',
  'user_role',
  'system_task',
  'status',
  'record_id',
  'record_type',
  'ip_address',
  'request_id',
  'error_code'
];

const formatNumber = (value) => Number(value || 0).toLocaleString();

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0
  });
  const [filterOptions, setFilterOptions] = useState({});
  const [showFilters, setShowFilters] = useState(true);

  const activeFilterCount = useMemo(() => {
    return trackedFilterKeys.reduce((count, key) => {
      const value = filters[key];

      if (Array.isArray(value)) {
        return value.length > 0 ? count + 1 : count;
      }

      return value !== '' && value !== null && value !== undefined ? count + 1 : count;
    }, 0);
  }, [filters]);

  const statisticsFilters = useMemo(() => {
    const rest = { ...filters };
    delete rest.page;
    delete rest.limit;
    delete rest.sort_by;
    delete rest.sort_direction;

    return rest;
  }, [filters]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await activityLogsAPI.getLogs(filters);
      setLogs(response.data.data || []);
      setPagination(response.data.pagination || {});
    } catch (err) {
      setError('Failed to fetch activity logs.');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await activityLogsAPI.getFilterOptions();
      setFilterOptions(response.data.data || {});
    } catch (err) {
      console.error('Error fetching activity log filter options:', err);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterApply = (nextFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...nextFilters,
      page: 1
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage
    }));
  };

  const handleRowsPerPageChange = (newLimit) => {
    setFilters((prev) => ({
      ...prev,
      limit: Number(newLimit),
      page: 1
    }));
  };

  const handleSortChange = (sortBy) => {
    setFilters((prev) => {
      const isSameColumn = prev.sort_by === sortBy;
      const nextDirection = isSameColumn && prev.sort_direction === 'desc' ? 'asc' : 'desc';

      return {
        ...prev,
        sort_by: sortBy,
        sort_direction: nextDirection,
        page: 1
      };
    });
  };

  const resolveExportFilename = (response, format) => {
    const disposition = response.headers?.['content-disposition'];
    const match = disposition?.match(/filename="?([^"]+)"?/i);

    if (match?.[1]) {
      return match[1];
    }

    const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
    return `activity_logs_${stamp}.${format === 'excel' ? 'xls' : 'csv'}`;
  };

  const handleExport = async (format) => {
    setExporting(format);
    setError('');

    try {
      const response = await activityLogsAPI.exportLogs({
        ...filters,
        page: undefined,
        limit: undefined,
        export_limit: 50000
      }, format);
      const blob = new Blob([response.data], {
        type: response.headers?.['content-type'] || 'application/octet-stream'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = resolveExportFilename(response, format);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Failed to export activity logs to ${format === 'excel' ? 'Excel' : 'CSV'}.`);
      console.error('Error exporting activity logs:', err);
    } finally {
      setExporting('');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fb', p: { xs: 2, md: 3 } }}>
      <Stack spacing={2.5}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <TableRows color="primary" />
                <Typography variant="h4" component="h1" fontWeight={800}>
                  Activity Logs
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  {formatNumber(pagination.from)}-{formatNumber(pagination.to)} of {formatNumber(pagination.total)} records
                </Typography>
                <Chip
                  size="small"
                  label={`${activeFilterCount} active filters`}
                  color={activeFilterCount > 0 ? 'primary' : 'default'}
                  variant={activeFilterCount > 0 ? 'filled' : 'outlined'}
                />
                <Chip
                  size="small"
                  label={`${filters.date_from} to ${filters.date_to}`}
                  variant="outlined"
                />
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Tooltip title="Refresh logs">
                <span>
                  <IconButton onClick={fetchLogs} disabled={loading}>
                    <Refresh />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant={showFilters ? 'contained' : 'outlined'}
                startIcon={<FilterAlt />}
                onClick={() => setShowFilters((value) => !value)}
              >
                Filters
              </Button>
              <Button
                variant="outlined"
                startIcon={exporting === 'csv' ? <CircularProgress size={16} /> : <FileDownload />}
                disabled={Boolean(exporting)}
                onClick={() => handleExport('csv')}
              >
                CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={exporting === 'excel' ? <CircularProgress size={16} /> : <GridOn />}
                disabled={Boolean(exporting)}
                onClick={() => handleExport('excel')}
              >
                Excel
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <LogsStatistics filters={statisticsFilters} />

        {showFilters && (
          <LogsFilters
            filters={filters}
            defaultFilters={DEFAULT_FILTERS}
            filterOptions={filterOptions}
            onApply={handleFilterApply}
            onClose={() => setShowFilters(false)}
          />
        )}

        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
              <CircularProgress />
            </Box>
          ) : (
            <LogsTable
              logs={logs}
              pagination={pagination}
              sortBy={filters.sort_by}
              sortDirection={filters.sort_direction}
              perPageOptions={filterOptions.per_page_options || [10, 20, 50, 100, 1000, 2000, 5000]}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onSortChange={handleSortChange}
            />
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default ActivityLogs;
